import { useNavigate } from 'react-router-dom';
import { Search, Bell, MessageCircle } from 'lucide-react';

import { CoreValueTicker } from '@/components/comms/CoreValueTicker';
import { useCommsUnread } from '@/hooks/useCommsUnread';
import { useUnreadNotificationCount } from '@/hooks/useNotifications';

/**
 * Mobile header. Hidden on desktop. Compact, branded, with a search bar that
 * opens the Cmd+K palette, the top-right message + notification chrome (Kate's
 * cross-viewport new-message signal), and a rotating core value beneath the
 * brand row when the org has defined any.
 */
export function MobileHeader() {
  const navigate = useNavigate();

  const unread = useCommsUnread().data;
  const chatUnread = unread?.chat.totalUnread ?? 0;
  const announcementUnread =
    (unread?.announcements.unread ?? 0) + (unread?.announcements.unacknowledged ?? 0);
  // ADR-048: the bell is the notification inbox; announcements are linked from it.
  const bellUnread = announcementUnread + (useUnreadNotificationCount().data ?? 0);

  return (
    <header className="sticky top-0 z-20 border-b border-kw-border bg-white md:hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex size-8 items-center justify-center rounded-kw-md bg-kw-cerulean font-bold text-white">
            K
          </span>
        </div>

        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('cmdk:open'))}
          className="flex h-9 flex-1 items-center gap-2 rounded-kw-md bg-kw-bg px-3 text-left text-[13px] text-kw-muted"
        >
          <Search className="size-4" />
          <span className="flex-1 truncate">Search…</span>
        </button>

        <button
          type="button"
          aria-label={chatUnread > 0 ? `Messages, ${String(chatUnread)} unread` : 'Messages'}
          onClick={() => {
            void navigate('/messages');
          }}
          className="relative rounded-kw-md p-2 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
        >
          <MessageCircle className="size-5" />
          {chatUnread > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 grid min-w-4 place-items-center rounded-kw-full bg-kw-danger-text px-1 text-[10px] font-semibold leading-4 text-white">
              {chatUnread > 9 ? '9+' : chatUnread}
            </span>
          ) : null}
        </button>

        <button
          type="button"
          aria-label={bellUnread > 0 ? 'Notifications, unread' : 'Notifications'}
          onClick={() => {
            void navigate('/notifications');
          }}
          className="relative rounded-kw-md p-2 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
        >
          <Bell className="size-5" />
          {bellUnread > 0 ? (
            <span className="absolute right-1.5 top-1.5 size-2 rounded-kw-full bg-kw-danger-text" />
          ) : null}
        </button>
      </div>

      {/* Rotating core value — renders (and takes space) only when values exist. */}
      <CoreValueTicker className="px-4 pb-2" />
    </header>
  );
}
